import { useRef, useState } from 'react'

import styled from 'styled-components'

import {
  fetchViewpointFlatImagePresignedPut,
  registerViewpointFlatImage,
  uploadViewpointFlatImageToS3,
} from '../../../../api/viewpointFlatImages'
import { validateFlatImageFile } from '../../../../utils/validateFlatImageFile'
import { LoadingIndicator } from '../../../common/LoadingIndicator'

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 200;
  display: flex;
  align-items: center;
  justify-content: center;
`

const Dialog = styled.div`
  background: #fff;
  border-radius: 4px;
  padding: 24px;
  width: min(420px, 90vw);
  box-shadow: 0 5px 30px rgba(0, 0, 0, 0.25);
`

const Title = styled.h3`
  margin: 0 0 16px;
  font-size: 18px;
  font-weight: 600;
`

const Field = styled.div`
  margin-bottom: 16px;
`

const Label = styled.label`
  display: block;
  font-size: 14px;
  margin-bottom: 6px;
  color: #4a4f57;
`

const Input = styled.input`
  width: 100%;
  padding: 8px;
  font-size: 14px;
  border: 1px solid #c5cad1;
  border-radius: 2px;
  box-sizing: border-box;
`

const ErrorText = styled.p`
  color: #c0392b;
  font-size: 13px;
  margin: 8px 0 0;
`

const Actions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 8px;
`

const ActionButton = styled.button<{ $primary?: boolean }>`
  padding: 8px 16px;
  font-size: 14px;
  border-radius: 2px;
  cursor: pointer;
  border: 1px solid ${p => (p.$primary ? '#1a6fd4' : '#c5cad1')};
  background: ${p => (p.$primary ? '#1a6fd4' : '#fff')};
  color: ${p => (p.$primary ? '#fff' : '#333')};

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

interface FlatImageUploadModalProps {
  projectId: string
  floorCode: string
  subPointId: number
  onClose: () => void
  onSuccess: () => void
}

export const FlatImageUploadModal = ({
  projectId,
  floorCode,
  subPointId,
  onClose,
  onSuccess,
}: FlatImageUploadModalProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [captureDate, setCaptureDate] = useState(() => {
    const d = new Date()
    return d.toISOString().slice(0, 10)
  })
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null
    setSelectedFile(file)
    setError(null)
  }

  const onSubmit = async () => {
    if (!selectedFile) {
      setError('Please choose an image file.')
      return
    }
    if (!captureDate) {
      setError('Please enter the image capture date.')
      return
    }

    setUploading(true)
    setError(null)

    try {
      const validation = await validateFlatImageFile(selectedFile)
      if (!validation.ok) {
        setError(validation.message)
        setUploading(false)
        return
      }

      const { presigned_url, s3_key } =
        await fetchViewpointFlatImagePresignedPut(
          projectId,
          floorCode,
          subPointId,
          selectedFile.name,
          validation.contentType,
        )

      await uploadViewpointFlatImageToS3(
        presigned_url,
        selectedFile,
        validation.contentType,
      )

      const takenOn = new Date(`${captureDate}T12:00:00`).toISOString()

      await registerViewpointFlatImage(projectId, floorCode, {
        sub_point_id: subPointId,
        s3_key,
        taken_on: takenOn,
      })

      onSuccess()
      onClose()
    } catch {
      setError('Upload failed. Please try again.')
    } finally {
      setUploading(false)
    }
  }

  return (
    <Overlay onClick={onClose}>
      <Dialog onClick={e => e.stopPropagation()}>
        <Title>Upload image</Title>
        <Field>
          <Label htmlFor="flat-image-capture-date">Capture date</Label>
          <Input
            id="flat-image-capture-date"
            type="date"
            value={captureDate}
            onChange={e => setCaptureDate(e.target.value)}
            disabled={uploading}
          />
        </Field>
        <Field>
          <Label htmlFor="flat-image-file">Image file (JPEG or PNG)</Label>
          <Input
            id="flat-image-file"
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,.jpg,.jpeg,.png"
            onChange={onFileChange}
            disabled={uploading}
          />
        </Field>
        {error && <ErrorText>{error}</ErrorText>}
        <Actions>
          <ActionButton type="button" onClick={onClose} disabled={uploading}>
            Cancel
          </ActionButton>
          <ActionButton
            type="button"
            $primary
            onClick={onSubmit}
            disabled={uploading}
          >
            {uploading ? 'Uploading…' : 'Upload'}
          </ActionButton>
        </Actions>
        {uploading && <LoadingIndicator />}
      </Dialog>
    </Overlay>
  )
}
